// src/pages/Manufacturing/manufacturingSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  fetchManufacturingNumber,
  createManufacturing,
  fetchManufacturingRecord,
} from "./manufacturingThunk";
import { currentDateTime } from "../../../lib/functions";
import Big from "big.js";

const bigAdd = (a, b) => Number(Big(a || 0).plus(b || 0).toFixed(13));
const bigMul = (a, b) => Number(Big(a || 0).times(b || 0).toFixed(13));

const blankRow = () => ({
  key: Date.now() + Math.random(),
  item_id: null,
  product: "",
  qty: 0,
  rate: 0,
  amount: 0,
  uom: "",
});

const initialState = {
  rawCart: [blankRow()],
  productionCart: [blankRow()],
  orderId: null,
  isEdit: false,
  isHydrating: false,

  footerValues: {
    labourCost: 0,
    otherCost: 0,
    totalCost: 0,
    description: "",
  },

  voucherNumber: null,
  entryDate: currentDateTime(),
  taxMode: "on_item",
  loading: false,
  success: null,
};

const manufacturingSlice = createSlice({
  name: "manufacturing",
  initialState,
  reducers: {
    resetManufacturing() {
      return { ...initialState, isEdit: false };
    },

    // ---------------- Raw Cart ----------------
    addRawRow(state) {
      state.rawCart.push(blankRow());
    },
    updateRawRow(state, action) {
      const { rowKey, updates } = action.payload;
      state.rawCart = state.rawCart.map((row) => {
        if (row.key !== rowKey) return row;
        // A manual qty/rate edit detaches this row from the recipe, so a
        // later "Apply Recipe" click won't silently overwrite it back.
        const detach =
          row.fromBom &&
          !("fromBom" in updates) &&
          ("qty" in updates || "rate" in updates);
        return {
          ...row,
          ...updates,
          ...(detach ? { fromBom: false } : {}),
        };
      });
    },
    deleteRawRow(state, action) {
      state.rawCart = state.rawCart.filter((r) => r.key !== action.payload);
      if (state.rawCart.length === 0) {
        state.rawCart.push(blankRow());
      }
    },

    // Apply Recipe (Perfume Industry BOM): seeds/updates one raw-material row
    // from a BOM component. Rows previously applied from a recipe are tagged
    // fromBom: true and matched by item_id on re-apply, so re-clicking "Apply
    // Recipe" updates quantities instead of duplicating rows; manually added
    // or hand-edited rows (no fromBom tag, or a different item_id) are left alone.
    applyRecipeRow(state, action) {
      const {
        item_id,
        product,
        qty,
        rate,
        unit_symbol,
        per_unit_id,
        warehouse_id,
      } = action.payload;
      const amount = bigMul(qty, rate);
      const rowData = {
        item_id,
        product,
        qty,
        rate,
        amount,
        uom: unit_symbol || "",
        unit_symbol: unit_symbol || "",
        per_unit_id: per_unit_id || 0,
        per_unit_symbol: unit_symbol || "",
        warehouse_id: warehouse_id || 0,
        fromBom: true,
      };

      const existingIndex = state.rawCart.findIndex(
        (r) => r.item_id === item_id && r.fromBom === true
      );
      if (existingIndex !== -1) {
        state.rawCart[existingIndex] = {
          ...state.rawCart[existingIndex],
          ...rowData,
        };
        return;
      }

      // A row for this raw material already exists but has been detached from
      // the recipe (manually edited — see updateRawRow's fromBom-clearing
      // logic below). Leave it alone: don't overwrite it, don't add a duplicate.
      const detachedIndex = state.rawCart.findIndex((r) => r.item_id === item_id);
      if (detachedIndex !== -1) {
        return;
      }

      const blankIndex = state.rawCart.findIndex((r) => !r.item_id);
      if (blankIndex !== -1) {
        state.rawCart[blankIndex] = {
          ...state.rawCart[blankIndex],
          ...rowData,
        };
      } else {
        state.rawCart.push({ key: Date.now() + Math.random(), ...rowData });
      }
    },

    // ---------------- Production Cart ----------------
    addProductionRow(state) {
      state.productionCart.push(blankRow());
    },
    updateProductionRow(state, action) {
      const { rowKey, updates } = action.payload;
      state.productionCart = state.productionCart.map((row) =>
        row.key === rowKey ? { ...row, ...updates } : row
      );
    },
    deleteProductionRow(state, action) {
      state.productionCart = state.productionCart.filter(
        (r) => r.key !== action.payload
      );
      if (state.productionCart.length === 0) {
        state.productionCart.push(blankRow());
      }
    },

    // ---------------- Footer ----------------
    setFooterValues(state, action) {
      state.footerValues = { ...state.footerValues, ...action.payload };
    },

    calculateTotals(state) {      
      if (state.isHydrating) return;
      console.log("slice state:::", JSON.parse(JSON.stringify(state)));
      
      const rawTotal = state.rawCart.reduce(
        (sum, i) => bigAdd(sum, bigMul(i.qty, i.rate)),
        0
      );

      const productionTotal = state.rawCart.reduce(
        (sum, i) => sum + (Number(i.qty) * Number(i.rate) || 0),
        0
      );

      const labour = Number(state.footerValues.labourCost) || 0;
      const other = Number(state.footerValues.otherCost) || 0;

      const totalCost = bigAdd(bigAdd(rawTotal, labour), other);

      state.footerValues = {
        ...state.footerValues,
        totalCost: Number(totalCost).toFixed(13)
      };
    },

    setEntryDate(state, action) {
      state.entryDate = action.payload;
    },
    setFormField(state, action) {
      const { field, value } = action.payload;
      state[field] = value;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchManufacturingNumber.fulfilled, (state, action) => {
        state.voucherNumber = action.payload;
      })
      .addCase(fetchManufacturingRecord.fulfilled, (state, action) => {
        state.isHydrating = true;

        const { record, rawCart, productionCart } = action.payload || {};
        if (!record) return;

        state.isEdit = true;
        state.rawCart = rawCart && rawCart.length > 0 ? rawCart : [blankRow()];
        state.productionCart =
          productionCart && productionCart.length > 0
            ? productionCart
            : [blankRow()];

        state.footerValues = {
          ...state.footerValues,
          labourCost: Number(record.labour_cost || 0).toFixed(13),
          otherCost: Number(record.others_cost || 0).toFixed(13),
          totalCost: Number(record.total_cost || 0).toFixed(13),
          description: record.narration || "",
        };
        state.voucherNumber = record.mf_voucher_no;
        state.entryDate = record.created_date;
        state.isHydrating = false;  // finish loading
      })
      .addCase(createManufacturing.pending, (state) => {
        state.loading = true;
      })
      .addCase(createManufacturing.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload;
      })
      .addCase(createManufacturing.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  resetManufacturing,
  addRawRow,
  updateRawRow,
  deleteRawRow,
  applyRecipeRow,
  addProductionRow,
  updateProductionRow,
  deleteProductionRow,
  setFooterValues,
  calculateTotals,
  setEntryDate,
  setFormField,
} = manufacturingSlice.actions;

export default manufacturingSlice.reducer;
